import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoHomeOutline, IoCubeOutline, IoBag, IoMenuOutline } from "react-icons/io5";

const StatusSeparator = ({ text }) => (
  <div className="flex items-center gap-3">
    <div className="h-px flex-1 bg-gray-300" />
    <span className="text-sm font-medium text-gray-600">{text}</span>
    <div className="h-px flex-1 bg-gray-300" />
  </div>
);

const OrderCard = ({ order, onClick }) => (
  <div className="flex gap-3 rounded-lg bg-white p-4 shadow-sm cursor-pointer" onClick={onClick}>
    <img
      loading="lazy"
      src={order.image}
      alt={order.productName}
      className="h-20 w-20 rounded-lg object-cover"
    />
    <div className="flex-1 text-left">
      <p className="text-sm text-gray-900">{order.productName}</p>
      <p className="mt-1 text-sm font-bold text-gray-900">₦{Number(order.productPrice || 0).toLocaleString("en-NG")}</p>
      <p className="mt-1 text-xs text-gray-600">Product ID: {order.productId}</p>
      <p className="mt-1 text-xs text-gray-600">Order ID: {order.orderId}</p>
      <p className="mt-1 text-xs text-gray-600">Quantity: {order.quantity}</p>
    </div>
  </div>
);

const VendorOrdersScreen = () => {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [ordersData, setOrdersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = [
    "All",
    "Pending",
    "Preparing For Delivery",
    "Placed For Delivery",
    "Delivering",
    "Delivered",
    "Confirmed Delivery",
    "Declined",
  ];

  useEffect(() => {
    const vendorToken = localStorage.getItem("vendorToken") || "<vendor JWT token>";
    const statusParam = selectedFilter === "All" ? "All" : selectedFilter;
    setLoading(true);
    setError(null);
    fetch(`${process.env.REACT_APP_API_URL}/api/vendors/orders?status=${encodeURIComponent(statusParam)}`, {
      headers: {
        Authorization: `Bearer ${vendorToken}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        if (!data.success) throw new Error(data.message || "Failed to fetch orders");
        // Group orders by status for UI
        const normalizedOrders = data.data.flatMap((order) =>
          (order.products || [])
            .filter((product) =>
              selectedFilter === "All"
                ? true
                : (product.orderStatus || "").toLowerCase() === selectedFilter.toLowerCase()
            )
            .map((product) => ({
              id: product.id || product._id || order.orderId,
              orderItemId: product.orderItemId,
              productName: product.productName || "",
              productPrice: product.price ?? "",
              productId: product.productId || "",
              orderId: product.orderNumber || order.orderNumber || "",
              quantity: product.quantity || "",
              image: product.productImages?.[0] || "/New%20Image.jpeg",
              status: product.orderStatus || order.status,
              createdAt: order.createdAt,
              buyer: order.buyerInfo,
          }))
        );

        setOrdersData([
          {
            status: statusParam,
            orders: normalizedOrders,
          },
        ]);
      })
      .catch(err => {
        setError(err.message);
        setOrdersData([]);
      })
      .finally(() => setLoading(false));
  }, [selectedFilter]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="fixed top-0 left-0 right-0 z-10 bg-white shadow-sm">
        <div className="px-6 py-4">
          <h1 className="text-center text-lg font-semibold text-gray-900">Orders</h1>
        </div>
        <div className="overflow-x-auto px-6 pb-3">
          <div className="flex gap-2">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition ${
                  selectedFilter === filter
                    ? "border border-[#19C463] bg-[#A3E7C1] text-[#19C463]"
                    : "border border-gray-300 bg-white text-gray-700"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="px-6 pt-32 pb-24">
        {loading ? (
          <div className="min-h-screen flex items-center justify-center text-gray-500">Loading orders...</div>
        ) : error ? (
          <div className="min-h-screen flex items-center justify-center text-red-500">{error}</div>
        ) : ordersData.length === 0 || !ordersData[0].orders.length ? (
          <div className="min-h-screen flex items-center justify-center text-gray-500">No orders found.</div>
        ) : (
          ordersData.map((section, index) => (
            <div key={section.status}>
              {index > 0 && <div className="mt-8" />}
              <StatusSeparator text={section.status} />
              <div className="mt-6 space-y-4">
                {section.orders.map((order, idx) => (
                <OrderCard
                  key={order.orderItemId || order.id || idx}
                  order={order}
                  onClick={() => navigate(`/view-order-vendor/${order.orderItemId || order.items?.[0]?.orderItemId}`)}
                />
                ))}
              </div>
            </div>
          ))
        )}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 flex items-center justify-around border-t border-gray-200 bg-white py-3">
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/vendor-home")}>
          <IoHomeOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Home</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/products")}>
          <IoCubeOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Product</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/vendor-orders")}>
          <IoBag className="h-6 w-6 text-[#19C463]" />
          <span className="text-xs font-bold text-[#19C463]">Orders</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/menu")}>
          <IoMenuOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Menu</span>
        </button>
      </nav>
    </div>
  );
};

export default VendorOrdersScreen;


